import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, onValue, off } from 'firebase/database';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import ProgressBar from "@ramonak/react-progress-bar";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import './style.css';

// Initialize Firebase
const firebaseConfig = {
    apiKey: "AIzaSyBOuu294zvRDZQVx2BLaVveXcsdDxpTjUA",
    authDomain: "rachpower-dad00.firebaseapp.com",
    databaseURL: "https://rachpower-dad00-default-rtdb.firebaseio.com",
    projectId: "rachpower-dad00",
    storageBucket: "rachpower-dad00.appspot.com",
    messagingSenderId: "364411865303",
    appId: "1:364411865303:web:7c4d04c39e6413eeb1c892"
};

const app = initializeApp(firebaseConfig);
const database = getDatabase(app);
const firestore = getFirestore(app);

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const Modal = ({ device, onClose }) => {
    const [progress, setProgress] = useState(0);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [generationProgress, setGenerationProgress] = useState(0);
    const [showGraph, setShowGraph] = useState(false);
    const [graphData, setGraphData] = useState(null);
    const [graphType, setGraphType] = useState('');
    const [isLoadingGraph, setIsLoadingGraph] = useState(false);
    const [graphProgress, setGraphProgress] = useState(0);
    const [showDownloadSuccess, setShowDownloadSuccess] = useState(false);

    useEffect(() => {
        if (!device) return;

        setProgress(15);
        let interval = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) return 0;
                return prev + 0.3;
            });
        }, 100);

        return () => clearInterval(interval);
    }, [device?.time_stamp]);

    if (!device) return null;

    const renderSignalBars = (signal) => {
        const heights = ['h-2', 'h-4', 'h-6', 'h-8', 'h-10'];
        const bars = [];
        for (let i = 1; i <= 5; i++) {
            bars.push(
                <div
                    key={i}
                    className={`${heights[i - 1]} w-1 ${i <= signal ? 'bg-green-700' : 'bg-gray-300'} mx-0.5 rounded-full transition-all duration-300`}
                ></div>
            );
        }
        return <div className="flex items-end">{bars}</div>;
    };

    const renderStatusBadge = (value, threshold, label) => {
        const isWarning = value >= threshold;
        return (
            <div className={`flex items-center gap-2 ${isWarning ? 'text-red-500' : 'text-green-600'}`}>
                <div className={`w-2 h-2 rounded-full ${isWarning ? 'bg-red-500' : 'bg-green-500'}`}></div>
                <span>{value} {isWarning && `(${label})`}</span>
            </div>
        );
    };

    const handleDownloadReport = async () => {
        if (!startDate || !endDate) {
            alert('Please select both start and end dates');
            return;
        }

        setIsLoading(true);
        setGenerationProgress(0);

        try {
            // Convert dates to timestamps for more efficient comparison
            const startDateTimestamp = startDate.getTime();
            const endDateTimestamp = endDate.getTime();

            // Query only the timestamps within our range
            const timestampsRef = collection(firestore, 'psuDevices');
            const q = query(
                timestampsRef,
                where('__name__', '>=', startDate.toISOString()),
                where('__name__', '<=', endDate.toISOString())
            );

            const timestampsSnapshot = await getDocs(q);
            const totalSteps = timestampsSnapshot.size;
            let completedSteps = 0;

            // Pre-allocate array with estimated size
            const deviceData = new Array(timestampsSnapshot.size);
            let dataIndex = 0;

            // Process timestamps in parallel using Promise.all
            const processPromises = timestampsSnapshot.docs.map(async (timestampDoc) => {
                const timestamp = new Date(timestampDoc.id).getTime();

                // Skip if timestamp is outside our range
                if (timestamp < startDateTimestamp || timestamp > endDateTimestamp) {
                    completedSteps++;
                    setGenerationProgress(Math.round((completedSteps / totalSteps) * 90));
                    return;
                }

                const devicesRef = collection(timestampDoc.ref, 'devices');
                const deviceDoc = await getDocs(devicesRef);

                // Find the matching device document
                const matchingDevice = deviceDoc.docs.find(doc => doc.id === device.id);

                if (matchingDevice) {
                    const data = matchingDevice.data();
                    deviceData[dataIndex++] = {
                        timestamp: new Date(timestamp).toLocaleString(),
                        leak_volt: data.leak_volt === 0 ? 0 : (data.leak_volt || 'N/A'),
                        mains_volt: data.mains_volt === 0 ? 0 : (data.mains_volt || 'N/A'),
                        temperature: data.temperature === 0 ? 0 : (data.temperature || 'N/A')
                    };
                }

                completedSteps++;
                setGenerationProgress(Math.round((completedSteps / totalSteps) * 90));
            });

            // Wait for all promises to resolve
            await Promise.all(processPromises);

            // Filter out any undefined entries and sort
            const filteredData = deviceData.filter(Boolean).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

            if (filteredData.length === 0) {
                throw new Error('No data found for this device in the selected date range');
            }

            // Set progress to 90% before PDF generation
            setGenerationProgress(90);

            // Generate PDF
            const doc = new jsPDF();

            // Add header information
            doc.setFontSize(16);
            doc.text('PSU Device Report', 14, 15);

            doc.setFontSize(10);
            doc.text(`Client: ${device.client || 'N/A'}`, 14, 25);
            doc.text(`Project: ${device.project || 'N/A'}`, 14, 30);
            doc.text(`Zone: ${device.zone || 'N/A'}`, 14, 35);
            doc.text(`Circle: ${device.circle || 'N/A'}`, 14, 40);
            doc.text(`Cluster: ${device.cluster || 'N/A'}`, 14, 45);
            doc.text(`City: ${device.city || 'N/A'}`, 14, 50);
            doc.text(`Site: ${device.site || 'N/A'}`, 14, 55);

            // Add table
            const tableColumn = ["Timestamp", "Leak Voltage", "Mains Voltage", "Temperature"];
            const tableRows = filteredData.map(data => [
                data.timestamp,
                `${data.leak_volt}V`,
                `${data.mains_volt}V`,
                `${data.temperature}°C`
            ]);

            autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: 65,
                theme: 'grid',
                styles: { fontSize: 8 },
                headStyles: { fillColor: [30, 58, 138] }
            });

            // Complete the progress
            setGenerationProgress(100);

            // Save the PDF
            doc.save(`PSU_Report_${device.id}_${startDate.toLocaleDateString()}_${endDate.toLocaleDateString()}.pdf`);
            
            // Show success animation
            setShowDownloadSuccess(true);
            setTimeout(() => {
                setShowDownloadSuccess(false);
                setShowDatePicker(false);
            }, 2000);
        } catch (error) {
            console.error('Error generating report:', error);
            alert(error.message || 'Error generating report. Please try again.');
        } finally {
            setIsLoading(false);
            setGenerationProgress(0);
        }
    };

    const fetchGraphData = async (type) => {
        setIsLoadingGraph(true);
        setGraphType(type);
        setShowGraph(true);
        setGraphProgress(0);

        try {
            const now = new Date();
            const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

            // Convert dates to timestamps for more efficient comparison
            const nowTimestamp = now.getTime();
            const oneDayAgoTimestamp = oneDayAgo.getTime();

            // Query only the timestamps within our range
            const timestampsRef = collection(firestore, 'psuDevices');
            const q = query(
                timestampsRef,
                where('__name__', '>=', oneDayAgo.toISOString()),
                where('__name__', '<=', now.toISOString())
            );

            const timestampsSnapshot = await getDocs(q);
            const totalSteps = timestampsSnapshot.size;
            let completedSteps = 0;

            // Pre-allocate array with estimated size
            const deviceData = new Array(timestampsSnapshot.size);
            let dataIndex = 0;

            // Process timestamps in parallel using Promise.all
            const processPromises = timestampsSnapshot.docs.map(async (timestampDoc) => {
                const timestamp = new Date(timestampDoc.id).getTime();

                // Skip if timestamp is outside our range
                if (timestamp < oneDayAgoTimestamp || timestamp > nowTimestamp) {
                    completedSteps++;
                    setGraphProgress(Math.round((completedSteps / totalSteps) * 90));
                    return;
                }

                const devicesRef = collection(timestampDoc.ref, 'devices');
                const deviceDoc = await getDocs(devicesRef);

                // Find the matching device document
                const matchingDevice = deviceDoc.docs.find(doc => doc.id === device.id);

                if (matchingDevice) {
                    const data = matchingDevice.data();
                    deviceData[dataIndex++] = {
                        timestamp: timestamp,
                        value: type === 'temperature' ? data.temperature :
                            type === 'leak_volt' ? data.leak_volt :
                                data.mains_volt
                    };
                }

                completedSteps++;
                setGraphProgress(Math.round((completedSteps / totalSteps) * 90));
            });

            // Wait for all promises to resolve
            await Promise.all(processPromises);

            // Filter out any undefined entries and sort
            const filteredData = deviceData.filter(Boolean).sort((a, b) => a.timestamp - b.timestamp);

            // Set progress to 100% before setting the graph data
            setGraphProgress(100);

            // Prepare data for chart
            const labels = filteredData.map(d => new Date(d.timestamp).toLocaleTimeString());
            const values = filteredData.map(d => d.value);

            // Determine color and label based on type
            const getGraphConfig = (type) => {
                switch (type) {
                    case 'temperature':
                        return {
                            label: 'Temperature',
                            borderColor: 'rgb(234, 179, 8)', // yellow-500
                            backgroundColor: 'rgba(234, 179, 8, 0.1)',
                            unit: '°C'
                        };
                    case 'leak_volt':
                        return {
                            label: 'Leak Voltage',
                            borderColor: 'rgb(239, 68, 68)', // red-500
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            unit: 'V'
                        };
                    default:
                        return {
                            label: 'Main Voltage',
                            borderColor: 'rgb(59, 130, 246)', // blue-500
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            unit: 'V'
                        };
                }
            };

            const config = getGraphConfig(type);

            setGraphData({
                labels,
                datasets: [{
                    label: config.label,
                    data: values,
                    borderColor: config.borderColor,
                    backgroundColor: config.backgroundColor,
                    fill: true,
                    tension: 0.4,
                    pointRadius: 3,
                    pointHoverRadius: 5,
                    borderWidth: 2
                }]
            });

            // Update chart options for temperature
            if (type === 'temperature') {
                chartOptions.scales.y.ticks.callback = function (value) {
                    return value + '°C';
                };
            } else {
                chartOptions.scales.y.ticks.callback = function (value) {
                    return value + 'V';
                };
            }
        } catch (error) {
            console.error('Error fetching graph data:', error);
            alert('Error loading graph data. Please try again.');
        } finally {
            setIsLoadingGraph(false);
            setGraphProgress(0);
        }
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                titleFont: {
                    size: 14
                },
                bodyFont: {
                    size: 13
                },
                callbacks: {
                    label: function (context) {
                        return `${context.dataset.label}: ${context.parsed.y}V`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                },
                ticks: {
                    font: {
                        size: 12
                    }
                }
            },
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 12
                    },
                    maxRotation: 45,
                    minRotation: 45
                }
            }
        },
        interaction: {
            intersect: false,
            mode: 'index'
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm z-50">
            {/* Success Animation Overlay */}
            {showDownloadSuccess && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 animate-fade-in">
                    <div className="bg-white rounded-2xl p-8 text-center transform transition-all duration-500 animate-scale-in">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-green-500 animate-checkmark" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Report Downloaded!</h2>
                        <p className="text-gray-600">Your report has been saved successfully</p>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-2xl shadow-2xl w-4/5 max-h-[90vh] flex flex-col">
                {/* Header - Fixed */}
                <div className="p-8 pb-0">
                    <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center gap-4">
                        {device.device === 1 ? (
                                <div className='text-white bg-red-600 font-semibold text-center rounded-lg shadow-lg px-6 py-2.5 transition-all hover:bg-red-700 flex items-center gap-2'>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                                    </svg>
                                    Wifi Module
                                </div>
                            ) : (
                                <div className='text-white font-semibold text-[14px] shadow-lg px-6 py-2.5 bg-yellow-500 text-center rounded-lg transition-all hover:bg-yellow-600 flex items-center gap-2'>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    GSM Module
                                </div>
                        )}
                        {renderSignalBars(device.signal)}
                        </div>
                        <h2 className="text-2xl font-bold text-[#28507B] border-b-2 border-blue-800 w-[50%] text-center pb-2">LIVE PSU DEVICE DATA</h2>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setShowDatePicker(!showDatePicker)}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-blue-700 transition-all duration-200 flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download Report
                            </button>
                            <button onClick={onClose} className="text-gray-500 hover:text-red-500 font-bold text-2xl transition-colors">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {showDatePicker && (
                        <div className="bg-gradient-to-br from-white to-blue-50 p-8 rounded-2xl shadow-xl mb-6 border border-blue-100">
                            <div className="flex flex-col gap-8">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-blue-100 rounded-xl">
                                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-800">Generate Device Report</h3>
                                        <p className="text-sm text-gray-500 mt-1">Select a date range to download the device data</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                        <div className="relative">
                                            <DatePicker
                                                selected={startDate}
                                                onChange={date => setStartDate(date)}
                                                selectsStart
                                                startDate={startDate}
                                                endDate={endDate}
                                                className="w-full border border-gray-200 rounded-xl pl-4 pr-10 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white hover:bg-blue-50"
                                                placeholderText="Select start date"
                                                dateFormat="MMMM d, yyyy"
                                                calendarClassName="rounded-xl shadow-lg border border-gray-100"
                                            />
                                            <div
                                                className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
                                                onClick={() => document.querySelector('input[name="start-date"]')?.click()}
                                            >
                                                <svg className="w-5 h-5 text-gray-400 hover:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                        <div className="relative">
                                            <DatePicker
                                                selected={endDate}
                                                onChange={date => setEndDate(date)}
                                                selectsEnd
                                                startDate={startDate}
                                                endDate={endDate}
                                                minDate={startDate}
                                                className="w-full border border-gray-200 rounded-xl pl-4 pr-10 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white hover:bg-blue-50"
                                                placeholderText="Select end date"
                                                dateFormat="MMMM d, yyyy"
                                                calendarClassName="rounded-xl shadow-lg border border-gray-100"
                                            />
                                            <div
                                                className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
                                                onClick={() => document.querySelector('input[name="end-date"]')?.click()}
                                            >
                                                <svg className="w-5 h-5 text-gray-400 hover:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-end">
                                        <button
                                            onClick={handleDownloadReport}
                                            disabled={isLoading}
                                            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-2.5 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
                                        >
                                            {isLoading ? (
                                                <>
                                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    <span>Generating...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                    </svg>
                                                    <span>Download</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {isLoading && (
                                    <div className="mt-6">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-gray-700">Generating Report</span>
                                            <span className="text-sm font-medium text-blue-600">{generationProgress}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                                            <div
                                                className="bg-gradient-to-r from-blue-500 to-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                                                style={{
                                                    width: `${generationProgress}%`,
                                                    backgroundSize: '200% 200%',
                                                    animation: 'gradient 2s ease infinite'
                                                }}
                                            />
                                        </div>
                                        <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                                            <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                            <span>Processing your data...</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {showGraph && (
                        <div className="mb-6 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                            <div className="flex justify-between items-center mb-4">
                                <h4 className="text-lg font-semibold text-gray-800">
                                    {graphType === 'leak_volt' ? 'Leak Voltage' : 'Main Voltage'} - Last 24 Hours
                                </h4>
                                <button
                                    onClick={() => setShowGraph(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="h-[300px]">
                                {isLoadingGraph ? (
                                    <div className="h-full flex flex-col items-center justify-center gap-4">
                                        <div className="w-full max-w-md">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-medium text-gray-700">Loading Graph Data</span>
                                                <span className="text-sm font-medium text-blue-600">{graphProgress}%</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                                                <div
                                                    className="bg-gradient-to-r from-blue-500 to-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                                                    style={{
                                                        width: `${graphProgress}%`,
                                                        backgroundSize: '200% 200%',
                                                        animation: 'gradient 2s ease infinite'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                            <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                            <span>Processing your data...</span>
                                        </div>
                                    </div>
                                ) : graphData ? (
                                    <Line data={graphData} options={chartOptions} />
                                ) : (
                                    <div className="h-full flex items-center justify-center text-gray-500">
                                        No data available
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="mb-6 w-3/4 mx-auto">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-green-100 rounded-lg">
                                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <span className="text-sm font-medium text-gray-700">Live Data Fetching</span>
                            </div>
                            <span className="text-sm font-medium text-green-600">{progress.toFixed(1)}%</span>
                        </div>
                        <div className="relative w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-300 ease-out"
                                style={{
                                    width: `${progress}%`,
                                    backgroundSize: '200% 200%',
                                    animation: 'gradient 2s ease infinite'
                                }}
                            />
                            <div
                                className="absolute top-0 left-0 h-full w-full bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.2)_50%,transparent_75%)] bg-[length:250%_250%] animate-shimmer"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                            <svg className="w-3 h-3 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            <span>Updating device metrics in real-time...</span>
                        </div>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto px-8 pb-8">
                    {/* System Status */}
                    <div className="bg-gray-50 rounded-xl p-6 shadow-lg mb-6">
                        <h3 className="text-lg font-semibold text-[#28507B] mb-4 pb-2 border-b border-gray-200">System Status</h3>
                        <div className="grid grid-cols-3 gap-8">
                            {/* Connection Status */}
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <h4 className="text-sm font-medium text-gray-500 mb-3">Connection Status</h4>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-gray-700">Connection Type</span>
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${device.device === 1 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                            {device.device === 1 ? 'WiFi' : 'GSM'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-gray-700">Signal Strength</span>
                                        <div className="flex items-center gap-2">
                                            {renderSignalBars(device.signal)}
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-gray-700">Last Updated</span>
                                        <span className="text-gray-600 text-sm">{device.lastUpdatedTime || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Voltage Status */}
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <h4 className="text-sm font-medium text-gray-500 mb-3">Voltage Status</h4>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-gray-700">Leak Voltage</span>
                                        <div className="flex items-center gap-2">
                                            {device.leak_volt >= 30 ? (
                                                <span className="text-red-500 flex items-center gap-1">
                                                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                                    {device.leak_volt}V
                                                </span>
                                            ) : (
                                                <span className="text-green-500 flex items-center gap-1">
                                                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                                    {device.leak_volt}V
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-gray-700">Main Voltage</span>
                                        <div className="flex items-center gap-2">
                                            {device.mains_volt >= 600 ? (
                                                <span className="text-red-500 flex items-center gap-1">
                                                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                                    {device.mains_volt}V
                                                </span>
                                            ) : (
                                                <span className="text-green-500 flex items-center gap-1">
                                                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                                    {device.mains_volt}V
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-gray-700">Battery Voltage</span>
                                        <span className="text-gray-600">{device.battery_voltage}V</span>
                                    </div>
                                </div>
                            </div>

                            {/* System Metrics */}
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <h4 className="text-sm font-medium text-gray-500 mb-3">System Metrics</h4>
                                <div className="space-y-4">
                                    <div
                                        className="flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-colors rounded-lg  py-1"
                                        onClick={() => fetchGraphData('temperature')}
                                    >
                                        <span className="font-medium text-gray-700">Temperature</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-600">{device.temperature}°C</span>
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-gray-700">Update Count</span>
                                        <span className="text-gray-600">{device.updateCount || 0}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-gray-700">Heat Index</span>
                                        <span className="text-gray-600">{device.heat_index}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        {/* Device Information */}
                        <div className="bg-gray-50 rounded-xl p-6 shadow-lg">
                            <h3 className="text-lg font-semibold text-[#28507B] mb-4 pb-2 border-b border-gray-200">Device Information</h3>
                            <div className="space-y-4">
                                {[
                                    { label: 'Client', value: device.client },
                                    { label: 'Project', value: device.project },
                                    { label: 'Zone', value: device.zone },
                                    { label: 'Circle', value: device.circle },
                                    { label: 'Cluster', value: device.cluster },
                                    { label: 'City', value: device.city },
                                    { label: 'Site', value: device.site },
                                    { label: 'Site Id', value: device.siteId },
                                    { label: 'IMEI No', value: device.imei }
                                ].map((row, index) => (
                                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                                        <span className="font-medium text-gray-700">{row.label}</span>
                                        <span className="text-gray-600">{row.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Device Metrics */}
                        <div className="bg-gray-50 rounded-xl p-6 shadow-lg">
                            <h3 className="text-lg font-semibold text-[#28507B] mb-4 pb-2 border-b border-gray-200">Device Metrics</h3>
                            <div className="space-y-4">
                                {[
                                    {
                                        label: 'Voltage Leak',
                                        value: device.leak_volt,
                                        warning: device.leak_volt >= 30,
                                        threshold: 30,
                                        warningLabel: 'Leak Voltage Warning',
                                        type: 'leak_volt',
                                        clickable: true
                                    },
                                    {
                                        label: 'Main Voltage',
                                        value: device.mains_volt,
                                        warning: device.mains_volt >= 600,
                                        threshold: 600,
                                        warningLabel: 'Main Voltage Warning',
                                        type: 'mains_volt',
                                        clickable: true
                                    },
                                    { label: 'Mains Current', value: device.mains_current },
                                    { label: 'Mains Power', value: device.mains_power },
                                    { label: 'Power Factor', value: device.power_factor },
                                    { label: 'Battery Voltage', value: device.battery_voltage },
                                    { label: 'Battery Current', value: device.current },
                                    { label: 'Heat Index', value: device.heat_index }
                                ].map((row, index) => (
                                    <div
                                        key={index}
                                        className={`flex justify-between items-center py-2 border-b border-gray-100 last:border-0 ${row.clickable ? 'cursor-pointer hover:bg-gray-100 transition-colors rounded-lg px-2' : ''}`}
                                        onClick={() => row.clickable && fetchGraphData(row.type)}
                                    >
                                        <span className="font-medium text-gray-700">{row.label}</span>
                                        {row.warning ? (
                                            <div className="flex items-center gap-2">
                                                {renderStatusBadge(row.value, row.threshold, row.warningLabel)}
                                                {row.clickable && (
                                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                    </svg>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-600">{row.value}</span>
                                                {row.clickable && (
                                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                    </svg>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add the required Tailwind CSS animations */}
            <style jsx>{`
                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes scale-in {
                    from { transform: scale(0.9); opacity: 0; }
                    to { transform: scale(1); opacity: 1; }
                }
                @keyframes checkmark {
                    0% { transform: scale(0); }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); }
                }
                .animate-fade-in {
                    animation: fade-in 0.3s ease-out;
                }
                .animate-scale-in {
                    animation: scale-in 0.5s ease-out;
                }
                .animate-checkmark {
                    animation: checkmark 0.5s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

const ManagePSUComponent = () => {
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [zones, setZones] = useState([]);
    const [circles, setCircles] = useState([]);
    const [clusters, setClusters] = useState([]);
    const [cities, setCities] = useState([]);
    const [sites, setSites] = useState([]);
    const [psuDevices, setPsuDevices] = useState([]);
    const [selectedClient, setSelectedClient] = useState("");
    const [selectedProject, setSelectedProject] = useState("");
    const [selectedZone, setSelectedZone] = useState("");
    const [selectedCircle, setSelectedCircle] = useState("");
    const [selectedCluster, setSelectedCluster] = useState("");
    const [selectedCity, setSelectedCity] = useState("");
    const [selectedSite, setSelectedSite] = useState("");
    const [imei, setImei] = useState("");
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const initializeData = async () => {
            try {
                await Promise.all([
                    fetchData('clients', setClients),
                    fetchData('zones', setZones)
                ]);
                // Add a small delay to ensure smooth animation
                setTimeout(() => {
                    setIsLoading(false);
                }, 1500);
            } catch (error) {
                console.error("Error initializing data:", error);
                setIsLoading(false);
            }
        };

        initializeData();
    }, []);

    useEffect(() => {
        if (selectedClient && selectedProject && selectedZone && selectedCircle && selectedCluster && selectedCity && selectedSite) {
            searchPsuDevices();
        }
    }, [selectedClient, selectedProject, selectedZone, selectedCircle, selectedCluster, selectedCity, selectedSite, imei]);

    useEffect(() => {
        if (selectedDevice) {
            const deviceRef = ref(database, `psuDevices/${selectedDevice.id}`);
            const lastUpdatedTimeRef = ref(database, `pastTime/${selectedDevice.id}/lastUpdatedTime`);
            const updateCountRef = ref(database, `pastTime/${selectedDevice.id}/updateCount`);

            const handleDeviceChange = (snapshot) => {
                const data = snapshot.val();
                if (data) {
                    setSelectedDevice(prev => ({ ...prev, ...data }));
                }
            };

            const handleLastUpdatedTimeChange = (snapshot) => {
                const lastUpdatedTime = snapshot.val();
                setSelectedDevice(prev => ({ ...prev, lastUpdatedTime }));
            };

            const handleUpdateCountChange = (snapshot) => {
                const updateCount = snapshot.val();
                setSelectedDevice(prev => ({ ...prev, updateCount }));
            };

            // Attach listeners
            const deviceListener = onValue(deviceRef, handleDeviceChange);
            const lastUpdatedTimeListener = onValue(lastUpdatedTimeRef, handleLastUpdatedTimeChange);
            const updateCountListener = onValue(updateCountRef, handleUpdateCountChange);

            // Cleanup listeners on component unmount or when selectedDevice changes
            return () => {
                off(deviceRef, 'value', handleDeviceChange);
                off(lastUpdatedTimeRef, 'value', handleLastUpdatedTimeChange);
                off(updateCountRef, 'value', handleUpdateCountChange);
            };
        }
    }, [selectedDevice]);


    const fetchData = async (path, setState) => {
        try {
            const response = await fetch(`https://rachpower-dad00-default-rtdb.firebaseio.com/${path}.json`);
            const data = await response.json();
            setState(data ? Object.entries(data).map(([key, value]) => ({ id: key, ...value })) : []);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const handleClientChange = async (e) => {
        const clientId = e.target.value;
        setSelectedClient(clientId);
        const client = clients.find(client => client.id === clientId);
        if (client && client.projects) {
            const projectsArray = Object.entries(client.projects).map(([key, value]) => ({ id: key, ...value }));
            setProjects(projectsArray);
        } else {
            setProjects([]);
        }
        searchPsuDevices();
    };

    const handleZoneChange = async (e) => {
        const zoneId = e.target.value;
        setSelectedZone(zoneId);
        const zone = zones.find(zone => zone.id === zoneId);
        if (zone && zone.circles) {
            const circlesArray = Object.entries(zone.circles).map(([key, value]) => ({ id: key, ...value }));
            setCircles(circlesArray);
        } else {
            setCircles([]);
        }
        searchPsuDevices();
    };

    const handleCircleChange = async (e) => {
        const circleId = e.target.value;
        setSelectedCircle(circleId);
        const circle = circles.find(circle => circle.id === circleId);
        if (circle && circle.clusters) {
            const clustersArray = Object.entries(circle.clusters).map(([key, value]) => ({ id: key, ...value }));
            setClusters(clustersArray);
        } else {
            setClusters([]);
        }
        searchPsuDevices();
    };

    const handleClusterChange = async (e) => {
        const clusterId = e.target.value;
        setSelectedCluster(clusterId);
        const cluster = clusters.find(cluster => cluster.id === clusterId);
        if (cluster && cluster.cities) {
            const citiesArray = Object.entries(cluster.cities).map(([key, value]) => ({ id: key, ...value }));
            setCities(citiesArray);
        } else {
            setCities([]);
        }
        searchPsuDevices();
    };

    const handleCityChange = async (e) => {
        const cityId = e.target.value;
        setSelectedCity(cityId);
        const city = cities.find(city => city.id === cityId);
        if (city && city.sites) {
            const sitesArray = Object.entries(city.sites).map(([key, value]) => ({ id: key, ...value }));
            setSites(sitesArray);
        } else {
            setSites([]);
        }
        searchPsuDevices();
    };

    const searchPsuDevices = async () => {
        try {
            const response = await fetch(`https://rachpower-dad00-default-rtdb.firebaseio.com/psuDevices.json`);
            const data = await response.json();
            const devicesArray = data ? Object.entries(data).map(([key, value]) => ({ id: key, ...value })) : [];
            const filteredDevices = devicesArray.filter(device =>
                (selectedClient === "" || device.client === clients.find(client => client.id === selectedClient)?.name) &&
                (selectedProject === "" || device.project === projects.find(project => project.id === selectedProject)?.name) &&
                (selectedZone === "" || device.zone === zones.find(zone => zone.id === selectedZone)?.name) &&
                (selectedCircle === "" || device.circle === circles.find(circle => circle.id === selectedCircle)?.name) &&
                (selectedCluster === "" || device.cluster === clusters.find(cluster => cluster.id === selectedCluster)?.name) &&
                (selectedCity === "" || device.city === cities.find(city => city.id === selectedCity)?.name) &&
                (selectedSite === "" || device.site === sites.find(site => site.id === selectedSite)?.siteName) &&
                (imei === "" || device.imei.includes(imei))
            );
            setPsuDevices(filteredDevices);
        } catch (error) {
            console.error("Error fetching PSU devices:", error);
        }
    };

    const openModal = (device) => {
        setSelectedDevice(device);
    };

    const closeModal = () => {
        setSelectedDevice(null);
    };

    return (
        <>
            {/* Loading Animation Overlay */}
            {isLoading && (
                <div className="fixed inset-0 bg-gradient-to-br from-blue-900 to-blue-800 flex items-center justify-center z-50 overflow-hidden">
                    {/* Decorative Animated Elements */}
                    <div className="absolute top-0 left-0 w-full h-full">
                        {/* Floating Circles */}
                        <div className="absolute top-1/4 left-1/4 w-16 h-16 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float-slow"></div>
                        <div className="absolute top-1/3 right-1/4 w-20 h-20 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float-slow-delay"></div>
                        <div className="absolute bottom-1/4 left-1/3 w-24 h-24 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float-slow-delay-2"></div>
                        <div className="absolute bottom-1/3 right-1/3 w-16 h-16 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float-slow"></div>

                        {/* Animated Lines */}
                        <div className="absolute top-0 left-0 w-full h-full">
                            <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent animate-line-sweep"></div>
                            <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent animate-line-sweep-delay"></div>
                            <div className="absolute top-3/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent animate-line-sweep-delay-2"></div>
                        </div>

                        {/* Corner Decorations */}
                        <div className="absolute top-8 left-8 w-16 h-16 border-t-2 border-l-2 border-blue-300 animate-corner-fade"></div>
                        <div className="absolute top-8 right-8 w-16 h-16 border-t-2 border-r-2 border-blue-300 animate-corner-fade"></div>
                        <div className="absolute bottom-8 left-8 w-16 h-16 border-b-2 border-l-2 border-blue-300 animate-corner-fade"></div>
                        <div className="absolute bottom-8 right-8 w-16 h-16 border-b-2 border-r-2 border-blue-300 animate-corner-fade"></div>
                    </div>

                    <div className="text-center flex flex-col items-center justify-center relative z-10">
                        <div className="relative w-32 h-32 mb-8 flex items-center justify-center">
                            {/* Outer Ring */}
                            <div className="absolute inset-0 border-4 border-blue-200 rounded-full animate-spin-slow"></div>
                            {/* Middle Ring */}
                            <div className="absolute inset-2 border-4 border-blue-300 rounded-full animate-spin-slow-reverse"></div>
                            {/* Inner Ring */}
                            <div className="absolute inset-4 border-4 border-blue-400 rounded-full animate-spin-slow"></div>
                            {/* Center Logo */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <svg className="w-14 h-14 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2 animate-fade-in">Loading PSU Dashboard</h2>
                        <p className="text-blue-100 animate-fade-in-delay">Please wait while we prepare your data...</p>
                    </div>

                    {/* Add the required Tailwind CSS animations */}
                    <style jsx>{`
                        @keyframes spin-slow {
                            from { transform: rotate(0deg); }
                            to { transform: rotate(360deg); }
                        }
                        @keyframes spin-slow-reverse {
                            from { transform: rotate(360deg); }
                            to { transform: rotate(0deg); }
                        }
                        @keyframes fade-in {
                            from { opacity: 0; transform: translateY(10px); }
                            to { opacity: 1; transform: translateY(0); }
                        }
                        @keyframes float-slow {
                            0% { transform: translate(0, 0) scale(1); }
                            50% { transform: translate(20px, -20px) scale(1.1); }
                            100% { transform: translate(0, 0) scale(1); }
                        }
                        @keyframes line-sweep {
                            0% { transform: translateX(-100%); opacity: 0; }
                            50% { opacity: 1; }
                            100% { transform: translateX(100%); opacity: 0; }
                        }
                        @keyframes corner-fade {
                            0% { opacity: 0; transform: scale(0.8); }
                            50% { opacity: 1; transform: scale(1); }
                            100% { opacity: 0; transform: scale(0.8); }
                        }
                        .animate-spin-slow {
                            animation: spin-slow 3s linear infinite;
                        }
                        .animate-spin-slow-reverse {
                            animation: spin-slow-reverse 4s linear infinite;
                        }
                        .animate-fade-in {
                            animation: fade-in 0.5s ease-out forwards;
                        }
                        .animate-fade-in-delay {
                            animation: fade-in 0.5s ease-out 0.3s forwards;
                            opacity: 0;
                        }
                        .animate-float-slow {
                            animation: float-slow 6s ease-in-out infinite;
                        }
                        .animate-float-slow-delay {
                            animation: float-slow 6s ease-in-out 2s infinite;
                        }
                        .animate-float-slow-delay-2 {
                            animation: float-slow 6s ease-in-out 4s infinite;
                        }
                        .animate-line-sweep {
                            animation: line-sweep 3s linear infinite;
                        }
                        .animate-line-sweep-delay {
                            animation: line-sweep 3s linear 1s infinite;
                        }
                        .animate-line-sweep-delay-2 {
                            animation: line-sweep 3s linear 2s infinite;
                        }
                        .animate-corner-fade {
                            animation: corner-fade 4s ease-in-out infinite;
                        }
                    `}</style>
                </div>
            )}

            {/* Main Content */}
            <div className={`p-6 bg-gray-50 min-h-screen transition-opacity duration-500 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
                <div className='p-8 rounded-2xl bg-white shadow-xl mb-8 border border-gray-100'>
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-blue-50 rounded-xl">
                                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                        </div>
                        <div>
                                <h1 className="text-2xl font-bold text-gray-800">Manage PSU Devices</h1>
                                <p className="text-sm text-gray-500 mt-1">Search and manage your PSU devices</p>
                            </div>
                        </div>
                        <button
                            onClick={searchPsuDevices}
                            className="bg-blue-600 text-white px-6 py-3 rounded-xl shadow-lg hover:bg-blue-700 transition-all duration-200 font-medium flex items-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            Search Devices
                        </button>
                    </div>
                    <div className="grid grid-cols-3 gap-6">
                        {/* Form Fields */}
                        {[
                            { label: 'Client', value: selectedClient, onChange: handleClientChange, options: clients },
                            { label: 'Project', value: selectedProject, onChange: (e) => setSelectedProject(e.target.value), options: projects },
                            { label: 'Zone', value: selectedZone, onChange: handleZoneChange, options: zones },
                            { label: 'Circle', value: selectedCircle, onChange: handleCircleChange, options: circles },
                            { label: 'Cluster', value: selectedCluster, onChange: handleClusterChange, options: clusters },
                            { label: 'City', value: selectedCity, onChange: handleCityChange, options: cities },
                            { label: 'Site', value: selectedSite, onChange: (e) => setSelectedSite(e.target.value), options: sites },
                            { label: 'IMEI', value: imei, onChange: (e) => setImei(e.target.value), isInput: true }
                        ].map((field, index) => (
                            <div key={index} className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">{field.label}</label>
                                {field.isInput ? (
                                    <input
                                        type="text"
                                        value={field.value}
                                        onChange={field.onChange}
                                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white"
                                        placeholder={`Enter ${field.label}`}
                                    />
                                ) : (
                                    <select
                                        value={field.value}
                                        onChange={field.onChange}
                                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white"
                                    >
                                        <option value="">All {field.label}s</option>
                                        {field.options.map(option => (
                                            <option key={option.id} value={option.id}>
                                                {option.name || option.siteName}
                                            </option>
                                ))}
                            </select>
                                )}
                            </div>
                        ))}
                    </div>
                        </div>

                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                    <div className="p-6 border-b border-gray-100 bg-[#1E3A8A]">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/10 rounded-lg">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                        </div>
                                <h2 className="text-lg font-semibold text-white">Device List</h2>
                        </div>
                            <div className="text-sm text-white/80">
                                Total Devices: {psuDevices.length}
                        </div>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className='bg-gray-50'>
                                <tr>
                                    {['Client', 'Project', 'Zone', 'Circle', 'Cluster', 'City', 'Site', 'IMEI', 'Actions'].map((header, index) => (
                                        <th key={index} className="py-4 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {header}
                                        </th>
                                    ))}
                            </tr>
                        </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {psuDevices.map(device => (
                                    <tr key={device.id} className="hover:bg-gray-50 transition-colors">
                                        {[
                                            device.client,
                                            device.project,
                                            device.zone,
                                            device.circle,
                                            device.cluster,
                                            device.city,
                                            device.site,
                                            device.imei
                                        ].map((value, index) => (
                                            <td key={index} className="py-4 px-6 text-sm text-gray-600 whitespace-nowrap">
                                                {value}
                                            </td>
                                        ))}
                                        <td className="py-4 px-6 whitespace-nowrap">
                                            <button
                                                onClick={() => openModal(device)}
                                                className="bg-green-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-green-700 transition-all duration-200 flex items-center gap-2 text-sm font-medium"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                                View Details
                                            </button>
                                    </td>
                                </tr>
                            ))}
                                {psuDevices.length === 0 && (
                                    <tr>
                                        <td colSpan="9" className="py-8 text-center text-gray-500">
                                            <div className="flex flex-col items-center gap-2">
                                                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <p className="text-sm">No devices found</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                        </tbody>
                    </table>
                    </div>
                </div>
                <Modal device={selectedDevice} onClose={closeModal} />
            </div>
        </>
    );
};

export default ManagePSUComponent;